import { Link, type ToOptions } from "@tanstack/react-router";
import { PrinterIcon } from "lucide-react";
import { forwardRef, useMemo } from "react";

import { StatIndicator } from "@/components/Indicators";
import { Badge } from "@/components/ui/badge";
import {
  ListItem,
  ListItemActions,
  ListItemLeft,
  ListItemRight,
} from "@/components/ui/list-item";
import { NavList, NavListItem } from "@/components/ui/nav-list";
import { Text } from "@/components/ui/text";
import { useNavSelected } from "@/utils/useNavStack";
import type { TestRunDetails } from "../types";
import { getProgressValues, worklistCodes } from "../utils";
import { PrintWorklistsButton } from "./PrintWorklistsButton";
import { TestProgressBar } from "./TestProgressBar";

type TestRunWorklist = TestRunDetails["testSpecs"][number];

export function WorklistList({
  run,
  worklists,
  loading,
}: {
  run: TestRunDetails;
  worklists: TestRunWorklist[];
  loading?: boolean;
}) {
  const { selected } = useNavSelected();

  return (
    <NavList
      items={worklists}
      aria-label="Worklists"
      autoFocus
      isLoading={loading}
      className="divide-y"
      initialIndex={worklists.findIndex((wl) => wl.id === selected)}
    >
      {(worklist) => (
        <NavListItem key={worklist.id} asChild>
          <WorklistListItem run={run} worklist={worklist} />
        </NavListItem>
      )}
    </NavList>
  );
}

const WorklistListItem = forwardRef<
  HTMLDivElement,
  {
    run: TestRunDetails;
    worklist: TestRunWorklist;
    className?: string;
  }
>(({ worklist, run, ...props }, ref) => {
  const testOrders = worklist.testOrders;

  const values = useMemo(() => getProgressValues(testOrders), [testOrders]);

  const toProps: ToOptions = {
    to: "/runs/$id/worklist/$testId",
    params: { id: run.id, testId: worklist.id },
    state: {
      href: {
        to: "/runs/$id",
        params: { id: run.id },
      },
      selected: worklist.id,
    },
  };

  const hasStat = useMemo(
    () =>
      worklist.testOrders.some((order) => order.accession.priority === "STAT"),
    [worklist.testOrders],
  );

  return (
    <ListItem ref={ref} asChild>
      <Link {...toProps} {...props}>
        <ListItemLeft className="min-w-0 flex-1">
          <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
            <Text size="2" className="min-w-0 truncate">
              {worklist.name}
            </Text>
            <Text size="1" muted className="shrink-0 font-mono tabular-nums">
              {worklistCodes(worklist)}
            </Text>
          </span>
          {hasStat && <StatIndicator />}
        </ListItemLeft>
        <ListItemRight className="gap-4">
          <Badge>{worklist.testOrders.length}</Badge>
          <PrintWorklistsButton
            run={run}
            worklists={[worklist]}
            size="icon-sm"
            variant="outline"
            className="hidden sm:inline-flex"
          >
            <PrinterIcon />
          </PrintWorklistsButton>
        </ListItemRight>
        <ListItemActions className="hidden lg:flex min-w-[200px]">
          <TestProgressBar {...values} />
        </ListItemActions>
      </Link>
    </ListItem>
  );
});
